<template>
    <div class="mechanic-asset-position-fields">
        <b-form-group label="Тип позиции">
            <b-form-radio-group
                v-model="accountingTypeModel"
                :options="accountingTypeOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                :disabled="disabled"
            />
        </b-form-group>

        <slot name="catalog" />

        <b-form-group label="Наименование позиции" label-for="mechanic-asset-position-display-name">
            <b-form-input
                id="mechanic-asset-position-display-name"
                :value="form.display_name"
                maxlength="500"
                autocomplete="off"
                :disabled="disabled"
                @input="updateField('display_name', $event)"
            />
        </b-form-group>

        <b-form-group v-if="showStatus" label="Статус">
            <b-form-select
                :value="form.status"
                :options="statusOptions"
                :disabled="disabled"
                @input="updateField('status', $event)"
            />
        </b-form-group>

        <b-row>
            <b-col md="6">
                <b-form-group label="Местонахождение">
                    <b-form-select
                        :value="form.location_type"
                        :options="locationTypeOptions"
                        :disabled="disabled"
                        @input="updateField('location_type', $event)"
                    />
                </b-form-group>
            </b-col>
            <b-col v-if="usesWarehouse" md="6">
                <b-form-group label="Склад / ремзона">
                    <v-select
                        :value="form.warehouse_id"
                        :options="warehouseOptions"
                        :reduce="option => option.value"
                        label="text"
                        placeholder="Не указан"
                        :loading="warehousesLoading"
                        :disabled="disabled"
                        @input="updateField('warehouse_id', $event)"
                    />
                </b-form-group>
            </b-col>
            <b-col v-else-if="form.location_type === 'object_group'" md="6">
                <b-form-group label="Группа объектов" description="Без назначения на конкретный объект">
                    <v-select
                        :value="form.object_group_id"
                        :options="objectGroupOptions"
                        :reduce="option => option.value"
                        label="text"
                        placeholder="Выберите группу объектов"
                        :disabled="disabled"
                        @input="updateField('object_group_id', $event)"
                    />
                </b-form-group>
            </b-col>
            <b-col v-else-if="usesObject" md="6">
                <b-form-group label="Объект">
                    <v-select
                        :value="form.building_process_id"
                        :options="objectOptions"
                        :reduce="option => option.value"
                        label="text"
                        placeholder="Не указан"
                        :loading="objectsLoading"
                        :disabled="disabled"
                        @input="updateField('building_process_id', $event)"
                    />
                    <small v-if="objectError" class="form-text text-danger">
                        {{ objectError }}
                    </small>
                </b-form-group>
            </b-col>
        </b-row>

        <b-form-group v-if="showResponsible" label="МОЛ">
            <v-select
                :value="form.responsible_user_id"
                :options="userOptions"
                :reduce="option => option.value"
                label="text"
                placeholder="Не указан"
                :disabled="disabled"
                @input="updateField('responsible_user_id', $event)"
            />
            <EmployeeSelectionPreview v-if="requestEmployeeContext && requestEmployeeContext.isEnabled()" :user-id="form.responsible_user_id" :options="userOptions" />
        </b-form-group>

        <b-row v-if="isQuantityAccounting">
            <b-col md="6">
                <b-form-group label="Количество / остаток">
                    <b-form-input
                        :value="form.quantity"
                        type="number"
                        :min="quantityMin"
                        step="0.001"
                        :disabled="disabled"
                        @input="updateNumberField('quantity', $event)"
                    />
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Единица измерения">
                    <b-form-input
                        :value="form.unit"
                        maxlength="50"
                        :disabled="disabled"
                        @input="updateField('unit', $event)"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <template v-else>
            <b-row>
                <b-col md="6">
                    <b-form-group label="Инвентарный номер">
                        <b-form-input
                            :value="form.inventory_number"
                            :disabled="disabled"
                            @input="updateField('inventory_number', $event)"
                        />
                    </b-form-group>
                </b-col>
                <b-col v-if="showSerialNumber" md="6">
                    <b-form-group label="Серийный номер">
                        <b-form-input
                            :value="form.serial_number"
                            :disabled="disabled"
                            @input="updateField('serial_number', $event)"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-form-group v-if="showPlateNumber" label="Госномер">
                <b-form-input
                    :value="form.plate_number"
                    :disabled="disabled"
                    @input="updateField('plate_number', $event)"
                />
            </b-form-group>
            <b-row v-if="showMeter">
                <b-col md="6">
                    <b-form-group label="Тип счетчика">
                        <b-form-select
                            :value="form.current_meter_type"
                            :options="meterTypeOptions"
                            :disabled="disabled"
                            @input="updateField('current_meter_type', $event)"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Показание счетчика">
                        <b-form-input
                            :value="form.current_meter_value"
                            type="number"
                            min="0"
                            step="0.001"
                            :disabled="disabled"
                            @input="updateNumberField('current_meter_value', $event)"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
        </template>

        <b-form-group v-if="showTechnicalCondition" label="Состояние">
            <v-select
                :value="form.technical_condition"
                :options="resolvedTechnicalConditionOptions"
                :create-option="createTextOption"
                taggable
                push-tags
                placeholder="Выберите или введите"
                :disabled="disabled"
                @input="updateField('technical_condition', $event)"
            >
                <template #no-options>
                    Введите свой вариант
                </template>
            </v-select>
        </b-form-group>
        <b-form-group v-if="showAccountingCompany" label="Компания владелец">
            <v-select
                :value="form.accounting_company_name"
                :options="resolvedAccountingCompanyOptions"
                :create-option="createTextOption"
                taggable
                push-tags
                placeholder="Выберите или введите"
                :disabled="disabled"
                @input="updateField('accounting_company_name', $event)"
            >
                <template #no-options>
                    Введите свой вариант
                </template>
            </v-select>
        </b-form-group>
        <b-form-group v-if="showComment" label="Комментарий">
            <AppTextarea
                :value="form.comment"
                :disabled="disabled"
                @input="updateField('comment', $event)"
            />
        </b-form-group>
    </div>
</template>

<script>
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
const DEFAULT_TECHNICAL_CONDITIONS = ['Новый', 'БУ'];

function mergeTextOptions(defaults, values) {
  const seen = {};
  return (defaults || []).concat(values || []).reduce((result, item) => {
    const value = String(item === null || item === undefined ? '' : item).trim();
    const key = value.toLocaleLowerCase();
    if (key && !seen[key]) {
      seen[key] = true;
      result.push(value);
    }
    return result;
  }, []);
}

export default {
  components: {EmployeeSelectionPreview},
  inject: {requestEmployeeContext: {default: null}},
  name: 'MechanicAssetPositionFormFields',
  props: {
    value: {
      type: Object,
      required: true
    },
    accountingType: {
      type: String,
      default: 'inventory'
    },
    accountingTypeOptions: {
      type: Array,
      default: () => []
    },
    statusOptions: {
      type: Array,
      default: () => []
    },
    locationTypeOptions: {
      type: Array,
      default: () => []
    },
    warehouseOptions: {
      type: Array,
      default: () => []
    },
    warehousesLoading: {
      type: Boolean,
      default: false
    },
    objectOptions: {
      type: Array,
      default: () => []
    },
    objectGroupOptions: {
      type: Array,
      default: () => []
    },
    objectsLoading: {
      type: Boolean,
      default: false
    },
    objectError: {
      type: String,
      default: ''
    },
    userOptions: {
      type: Array,
      default: () => []
    },
    meterTypeOptions: {
      type: Array,
      default: () => []
    },
    accountingCompanyOptions: {
      type: Array,
      default: () => []
    },
    quantityMin: {
      type: Number,
      default: 0
    },
    showStatus: {
      type: Boolean,
      default: true
    },
    showResponsible: {
      type: Boolean,
      default: true
    },
    showSerialNumber: {
      type: Boolean,
      default: true
    },
    showPlateNumber: {
      type: Boolean,
      default: true
    },
    showMeter: {
      type: Boolean,
      default: true
    },
    showTechnicalCondition: {
      type: Boolean,
      default: true
    },
    showAccountingCompany: {
      type: Boolean,
      default: true
    },
    showComment: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    form() {
      return this.value || {};
    },
    accountingTypeModel: {
      get() {
        return this.accountingType;
      },
      set(value) {
        this.$emit('update:accountingType', value);
        this.$emit('accounting-type-change', value);
      }
    },
    isQuantityAccounting() {
      return this.accountingType === 'quantity';
    },
    usesWarehouse() {
      return ['warehouse', 'repair', 'external_repair'].indexOf(this.form.location_type) !== -1;
    },
    usesObject() {
      return this.form.location_type === 'object';
    },
    resolvedTechnicalConditionOptions() {
      return DEFAULT_TECHNICAL_CONDITIONS;
    },
    resolvedAccountingCompanyOptions() {
      return mergeTextOptions([], this.accountingCompanyOptions);
    }
  },
  methods: {
    createTextOption(value) {
      return String(value || '').trim();
    },
    updateField(field, value) {
      this.$emit('input', Object.assign({}, this.form, {[field]: value}));
    },
    updateNumberField(field, value) {
      const normalized = value === '' || value === null || value === undefined
          ? null
          : Number(value);
      this.updateField(field, normalized);
    }
  }
};
</script>
